import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAutoRefresh } from "@/lib/autoRefresh";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, formatDate, PAYMENT_TYPE_LABEL, BILL_STATUS_META } from "@/lib/format";
import { FileUploader } from "@/components/FileUploader";
import { ReceiptText, Plus, Trash2, Loader2, ClipboardList, Wallet, ExternalLink, CheckCircle2, ChevronsUpDown, ChevronDown, Check, Users2, Pencil, AlertTriangle, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { toast } from "sonner";
import { EditHistory } from "@/components/AdminEdit";
import { AdminActions, AdminDeleteButton } from "@/components/AdminActions";
import { useBulk, BulkCheck, BulkCheckAll, BulkBar } from "@/components/BulkDelete";

const INST_FIELDS = [{ k: "label", l: "Label Termin" }, { k: "date", l: "Tanggal", t: "date" }, { k: "amount", l: "Nominal", t: "money" }];
import { RupiahInput } from "@/components/RupiahInput";
import { PartialPayments, PartialPayDialog, PayCheck, BulkPayBar } from "@/components/BillPayments";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const SCHEDULE_LABELS = { harian: "Setiap Hari", mingguan: "Setiap Minggu", jatuh_tempo: "Menunggu Jatuh Tempo" };

function BillBadge({ status, partial }) {
  const m = partial && status !== "paid" ? { label: "Sebagian Dibayar", cls: "bg-violet-100 text-violet-800 border-violet-200" } : (BILL_STATUS_META[status] || BILL_STATUS_META.pending);
  return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.cls}`}>{m.label}</span>;
}

function VendorCombobox({ vendors, value, onChange, onVendorCreated }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const q = query.trim().toLowerCase();
  const filtered = vendors.filter((v) => v.name.toLowerCase().includes(q));
  const exactExists = vendors.some((v) => v.name.toLowerCase() === q);

  const createVendor = async () => {
    const name = query.trim();
    if (!name) return;
    setCreating(true);
    try {
      const r = await api.post("/vendors", { name });
      toast.success(`Vendor "${name}" ditambahkan.`);
      onVendorCreated?.(r.data);
      onChange(name);
      setOpen(false); setQuery("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" className="w-full justify-between mt-1.5 font-normal" data-testid="bill-vendor-combobox">
          {value || <span className="text-muted-foreground">Pilih vendor...</span>}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Cari / ketik nama vendor..." value={query} onValueChange={setQuery} data-testid="bill-vendor-search" />
          <CommandList>
            {filtered.length === 0 && !query.trim() && <CommandEmpty>Ketik untuk mencari vendor.</CommandEmpty>}
            <CommandGroup>
              {filtered.map((v) => (
                <CommandItem key={v.id} value={v.id} onSelect={() => { onChange(v.name); setOpen(false); setQuery(""); }} data-testid={`bill-vendor-option-${v.id}`}>
                  <Check className={`h-4 w-4 mr-2 ${value === v.name ? "opacity-100" : "opacity-0"}`} />
                  {v.name}
                </CommandItem>
              ))}
            </CommandGroup>
            {query.trim() && !exactExists && (
              <CommandItem value="__create" onSelect={createVendor} disabled={creating} className="text-brand" data-testid="bill-vendor-create-item">
                <Plus className="h-4 w-4 mr-2" /> Tambah Vendor Baru "{query.trim()}"
              </CommandItem>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ManageVendorsDialog({ vendors, onChange }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [limit, setLimit] = useState("");
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const resetForm = () => { setName(""); setPhone(""); setNote(""); setLimit(""); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nama vendor wajib diisi"); return; }
    setBusy(true);
    const payload = { name, phone, note, credit_limit: Number(limit) || 0 };
    try {
      if (editId) { await api.put(`/vendors/${editId}`, payload); toast.success("Vendor diperbarui."); }
      else { await api.post("/vendors", payload); toast.success("Vendor ditambahkan."); }
      resetForm(); onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const startEdit = (v) => { setEditId(v.id); setName(v.name); setPhone(v.phone || ""); setNote(v.note || ""); setLimit(v.credit_limit ? String(v.credit_limit) : ""); };
  const del = async (v) => {
    try { await api.delete(`/vendors/${v.id}`); toast.success("Vendor dihapus."); if (editId === v.id) resetForm(); onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-brand text-brand" data-testid="manage-vendors-btn"><Users2 className="h-4 w-4 mr-2" /> Kelola Vendor</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="manage-vendors-dialog">
        <DialogHeader><DialogTitle>Master Vendor</DialogTitle>
          <DialogDescription>Tambah, ubah, atau hapus data vendor yang dipakai pada tagihan.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nama Vendor</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="vendor-name-input" placeholder="PT ..." /></div>
            <div><Label>Telepon / Kontak</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" data-testid="vendor-phone-input" placeholder="08..." /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Limit Kredit</Label>
              <RupiahInput min="0" value={limit} onChange={(e) => setLimit(e.target.value)} className="mt-1.5 font-mono" data-testid="vendor-limit-input" placeholder="0" />
              {Number(limit) > 0 && <p className="text-[11px] text-brand font-mono mt-0.5">{formatRupiah(Number(limit))}</p>}
            </div>
            <div><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid="vendor-note-input" placeholder="Opsional" /></div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="submit-vendor-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Perbarui" : "Tambah Vendor")}
            </Button>
            {editId && <Button type="button" variant="ghost" onClick={resetForm} data-testid="cancel-vendor-edit-btn">Batal</Button>}
          </div>
        </form>
        <div className="border-t pt-3 max-h-64 overflow-y-auto space-y-1.5" data-testid="vendor-list">
          {vendors.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">Belum ada vendor.</p> :
            vendors.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2" data-testid={`vendor-item-${v.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm">{v.name}</p>
                  {(v.phone || v.note) && <p className="text-xs text-muted-foreground truncate">{[v.phone, v.note].filter(Boolean).join(" · ")}</p>}
                  {v.credit_limit > 0 && <p className="text-[11px] text-brand font-mono">Limit kredit: {formatRupiah(v.credit_limit)}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => startEdit(v)} data-testid={`edit-vendor-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(v)} data-testid={`delete-vendor-${v.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}


const addDays = (base, n) => {
  if (!base || n === "" || Number(n) < 0) return "";
  const [y, m, d] = base.split("-").map(Number);
  const dt = new Date(y, m - 1, d + Number(n));
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const daysBetween = (a, b) => (a && b ? Math.round((new Date(b) - new Date(a)) / 86400000) : "");

function EditBillDialog({ bill, vendors, companies, onDone, staff }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);
  useEffect(() => {
    if (open) setF({ vendor_name: bill.vendor_name || "", company_id: bill.company_id || "", payment_type: bill.payment_type,
      due_date: (bill.due_date || "").slice(0, 10), description: bill.description || "",
      due_days: String(bill.due_days ?? daysBetween((bill.notas?.[0]?.order_date || "").slice(0, 10), (bill.due_date || "").slice(0, 10))),
      notas: (bill.notas?.length ? bill.notas : [{ nomor_nota: "", amount: bill.amount, order_date: "" }])
        .map((n) => ({ nomor_nota: n.nomor_nota || "", amount: n.amount ?? "", order_date: (n.order_date || "").slice(0, 10) })) });
  }, [open, bill]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setNota = (i, k, v) => setF((p) => ({ ...p, notas: p.notas.map((n, idx) => (idx === i ? { ...n, [k]: v } : n)) }));
  const total = (f?.notas || []).reduce((s, n) => s + (Number(n.amount) || 0), 0);
  const effDays = f?.payment_type === "harian" ? 0 : f?.due_days;
  const staffDue = staff && f ? addDays(f.notas[0]?.order_date, effDays) : "";
  const save = async (e) => {
    e.preventDefault();
    if (staff && !f.notas[0]?.order_date) { toast.error("Isi Tanggal Pemesanan nota pertama"); return; }
    if (staff && !staffDue) { toast.error("Isi Jatuh Tempo (jumlah hari)"); return; }
    setBusy(true);
    try {
      const { due_days: _dd, ...rest } = f;
      const payload = { ...rest, company_id: f.company_id || null,
        notas: f.notas.map((n) => ({ ...n, amount: Number(n.amount) || 0, order_date: n.order_date || null })) };
      if (staff) Object.assign(payload, { due_date: staffDue, due_days: Number(effDays) });
      await api.put(staff ? `/vendor-bills/${bill.id}` : `/admin/vendor-bills/${bill.id}`, payload);
      toast.success("Tagihan diperbarui."); setRk((x) => x + 1); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 border-brand/40 text-brand" data-testid={staff ? `edit-bill-${bill.id}` : `admin-edit-bill-${bill.id}`}>
          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-bill-dialog">
        <DialogHeader><DialogTitle>Edit Tagihan — {bill.vendor_name}</DialogTitle>
          <DialogDescription>{staff ? "Perubahan dicatat di riwayat." + (bill.status === "paid" || (bill.payment_installments || []).some((i) => i.paid) ? " Tagihan sudah ada pembayaran; pembayaran tercatat tidak berubah, Direktur akan diberi notifikasi." : bill.status === "planned" ? " Rencana pembayaran Direktur tetap, Direktur akan diberi notifikasi." : "") : "Perubahan oleh IT Admin dicatat di riwayat. Status & alur pembayaran tidak berubah."}</DialogDescription>
        </DialogHeader>
        {f && (
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Vendor</Label>
                <select value={f.vendor_name} onChange={(e) => set("vendor_name", e.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="edit-bill-vendor">
                  {!vendors.some((v) => v.name === f.vendor_name) && <option value={f.vendor_name}>{f.vendor_name}</option>}
                  {vendors.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <Label>PT</Label>
                <select value={f.company_id} onChange={(e) => set("company_id", e.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="edit-bill-company">
                  {!f.company_id && <option value="">Tanpa PT</option>}
                  {f.company_id && !companies.some((c) => c.id === f.company_id) && <option value={f.company_id}>{bill.company_name}</option>}
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Jenis Pembayaran</Label>
                <select value={f.payment_type} onChange={(e) => set("payment_type", e.target.value)} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="edit-bill-type">
                  <option value="harian">Harian</option><option value="jatuh_tempo">Jatuh Tempo</option><option value="bilyet_giro">Bilyet Giro (BG)</option>
                </select>
              </div>
              {staff ? (
                <div><Label>Jatuh Tempo (hari)</Label>
                  <div className="relative mt-1.5">
                    <Input type="number" min="0" value={f.payment_type === "harian" ? 0 : f.due_days} disabled={f.payment_type === "harian"}
                      onChange={(e) => set("due_days", e.target.value)} className="pr-12 font-mono" data-testid="edit-bill-due-days" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">hari</span>
                  </div>
                </div>
              ) : (
                <div><Label>Jatuh Tempo / Tgl BG</Label><Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5" data-testid="edit-bill-due" /></div>
              )}
            </div>
            {staff && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs" data-testid="edit-bill-due-preview">
                {staffDue ? <span>{f.payment_type === "bilyet_giro" ? "Tanggal Efektif BG" : "Tanggal Jatuh Tempo"}: <b className="text-brand">{formatDate(staffDue)}</b> <span className="text-muted-foreground">(Tgl Pemesanan nota pertama + {Number(effDays)} hari)</span></span>
                  : <span className="text-muted-foreground">Isi Tgl Pemesanan nota pertama dan jumlah hari.</span>}
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Nota</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => set("notas", [...f.notas, { nomor_nota: "", amount: "", order_date: "" }])} data-testid="edit-bill-add-nota"><Plus className="h-3.5 w-3.5 mr-1" /> Nota</Button>
              </div>
              {f.notas.map((n, i) => (
                <div key={i} className="relative grid grid-cols-2 sm:grid-cols-[1.1fr_1.1fr_1fr_auto] gap-2 items-center rounded-lg border p-2 sm:border-0 sm:p-0" data-testid={`edit-bill-nota-${i}`}>
                  <Input placeholder="No. Nota" value={n.nomor_nota} onChange={(e) => setNota(i, "nomor_nota", e.target.value)} className="order-1 col-span-2 sm:col-span-1 w-[calc(100%-2.75rem)] sm:w-full" data-testid={`edit-bill-nota-nomor-${i}`} />
                  <RupiahInput value={n.amount} onChange={(e) => setNota(i, "amount", e.target.value)} className="font-mono order-4 sm:order-2 min-w-0" data-testid={`edit-bill-nota-amount-${i}`} />
                  <Input type="date" title="Tgl Pemesanan" value={n.order_date} onChange={(e) => setNota(i, "order_date", e.target.value)} className="order-3 min-w-0" data-testid={`edit-bill-nota-date-${i}`} />
                  <Button type="button" size="icon" variant="ghost" className="order-2 sm:order-4 absolute top-2 right-2 sm:static" disabled={f.notas.length === 1} onClick={() => set("notas", f.notas.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              ))}
              <p className="text-sm text-right font-mono text-brand" data-testid="edit-bill-total">Total {formatRupiah(total)}</p>
            </div>
            <div><Label>Keterangan</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" data-testid="edit-bill-desc" /></div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="save-edit-bill">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Perubahan"}
              </Button>
            </DialogFooter>
          </form>
        )}
        <div className="pt-3 border-t"><EditHistory entityType="vendor_bill" entityId={bill.id} refreshKey={rk} bare /></div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteBillButton({ bill, onDone }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try { await api.delete(`/vendor-bills/${bill.id}`); toast.success("Tagihan dihapus."); onDone(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 hover:bg-rose-50" disabled={busy} data-testid={`delete-bill-btn-${bill.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus tagihan {bill.vendor_name}?</AlertDialogTitle>
          <AlertDialogDescription>
            {formatRupiah(bill.amount)} · {bill.company_name || "Tanpa PT"}. Tagihan akan dihapus permanen dan tercatat di Log Audit.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Batal</AlertDialogCancel>
          <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-bill-${bill.id}`}>Hapus</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function BillRow({ b, canPlan, canPay, canDelete, onDone, vendors, companies, bulk, isStaff, paySel, togglePay }) {
  const markNotDup = async (vendor_name, nomor_nota) => {
    if (!window.confirm(`Tandai nota "${nomor_nota}" (${vendor_name}) sebagai sah / bukan duplikat?`)) return;
    try { await api.post("/vendor-bills/dup-whitelist", { vendor_name, nomor_nota }); toast.success("Nota ditandai sah."); onDone(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const canEditOwn = isStaff;
  const notas = [...(b.notas || [])].sort((a, c) => (a.order_date || "").localeCompare(c.order_date || ""));
  return (
    <div className="px-4 py-3" data-testid={`bill-row-${b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex items-start gap-3">
        {canPay && b.status !== "paid" && <span className="pt-1"><PayCheck checked={!!paySel[b.id]} onChange={() => togglePay(b)} testId={`pay-check-${b.id}`} /></span>}
        <span className="pt-1"><BulkCheck bulk={bulk} k={b.id} item={{ kind: "vendor-bills", id: b.id }} testId={`bulk-bill-${b.id}`} /></span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold">{formatRupiah(b.amount)}</span>
            <BillBadge status={b.status} partial={b.payment_state === "sebagian"} />
            {(b.duplicate_notas || []).length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-md font-semibold bg-rose-100 text-rose-800 border border-rose-200 inline-flex items-center gap-1" data-testid={`bill-dup-badge-${b.id}`}><AlertTriangle className="h-3 w-3" /> Nota Duplikat</span>}
            <span className="text-xs text-muted-foreground">{PAYMENT_TYPE_LABEL[b.payment_type]} · jatuh tempo {formatDate(b.due_date)}</span>
          </div>
          <p className="text-xs mt-0.5" data-testid={`bill-company-${b.id}`}>
            <span className="font-medium text-brand">{b.company_name || "Tanpa PT"}</span>
            {b.company_account && <span className="font-mono text-muted-foreground"> · Rek. {b.company_account}</span>}
          </p>
          {(b.duplicate_notas || []).length > 0 && (
            <div className="mt-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-800 space-y-0.5" data-testid={`bill-dup-detail-${b.id}`}>
              {b.duplicate_notas.map((d, i) => (
                <p key={i} className="flex flex-wrap items-center gap-x-2"><span>Nota <b>{d.nomor_nota}</b>: {d.same_bill ? "tercatat lebih dari sekali di tagihan ini" : ""}{d.same_bill && d.others.length ? "; " : ""}{d.others.map((o) => `sama dengan tagihan ${o.company_name || "Tanpa PT"} · ${formatRupiah(o.amount)} (input ${formatDate(o.created_at)})`).join("; ")}</span>
                  {isStaff && <button type="button" onClick={() => markNotDup(b.vendor_name, d.nomor_nota)} className="rounded border border-rose-300 bg-white px-1.5 py-0.5 font-semibold hover:bg-rose-100" data-testid={`bill-dup-mark-${b.id}-${i}`}>Tandai Bukan Duplikat</button>}</p>
              ))}
            </div>
          )}
          {b.payment_state === "sebagian" && (
            <p className="text-xs mt-0.5" data-testid={`bill-partial-summary-${b.id}`}>Terbayar <b className="font-mono text-emerald-700">{formatRupiah(b.paid_amount)}</b> · Sisa <b className="font-mono text-rose-700">{formatRupiah(b.remaining_amount)}</b></p>
          )}
          {b.description && <p className="text-xs text-muted-foreground mt-0.5">{b.description}</p>}
          {notas.length > 0 && (
            <div className="mt-1.5 space-y-0.5" data-testid={`bill-notas-${b.id}`}>
              {notas.map((n, i) => (
                <div key={i} className="text-[11px] text-muted-foreground flex gap-2 flex-wrap">
                  <span className="font-medium text-foreground/80">{n.nomor_nota || `Nota ${i + 1}`}</span>
                  <span>· {formatRupiah(n.amount)}</span>
                  {n.order_date && <span>· pesan {formatDate(n.order_date)}</span>}
                </div>
              ))}
            </div>
          )}
          {b.status === "planned" && b.payment_schedule && (
            <p className="text-[11px] text-muted-foreground mt-1" data-testid={`bill-schedule-${b.id}`}>
              {SCHEDULE_LABELS[b.payment_schedule] || b.payment_schedule}
              {b.payment_installments?.length > 0 ? ` · ${b.payment_installments.length} termin` : ""}
            </p>
          )}
          {b.status === "paid" && b.pay_account && (
            <p className="text-[11px] mt-1 text-emerald-800" data-testid={`bill-pay-account-${b.id}`}>
              Dibayar dari: <span className="font-mono">{b.pay_account.company_name ? `${b.pay_account.company_name} · ` : ""}{payAccLabel(b.pay_account)}</span>
              {b.paid_by ? ` · oleh ${b.paid_by}` : ""}{b.paid_at ? ` · ${formatDate(b.paid_at)}` : ""}
            </p>
          )}
          <PartialPayments b={b} />
          {(b.payment_installments || []).some((it) => it.paid && it.pay_account) && (
            <div className="mt-1 space-y-0.5" data-testid={`bill-inst-accounts-${b.id}`}>
              {b.payment_installments.map((it, i) => it.paid && it.pay_account ? (
                <p key={i} className="text-[11px] text-emerald-800">{it.label || `Termin ${i + 1}`} dibayar dari: <span className="font-mono">{payAccLabel(it.pay_account)}</span></p>
              ) : null)}
            </div>
          )}
          {canDelete && (b.payment_installments || []).length > 0 && (
            <div className="mt-1.5 space-y-0.5" data-testid={`admin-installments-${b.id}`}>
              {b.payment_installments.map((it, i) => (
                <div key={i} className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground/80">{it.label || `Termin ${i + 1}`}</span>
                  <span>· {formatRupiah(it.amount)} · {it.date ? formatDate(it.date) : "-"}</span>
                  <span className={it.paid ? "text-emerald-700" : ""}>{it.paid ? `Dibayar ${formatDate(it.paid_at)}` : "Belum dibayar"}</span>
                  {it.paid && <AdminDeleteButton path={`/admin/reset/bill-payment/${b.id}?key=${i}`} onDone={onDone} label="Hapus Bayar" testId={`admin-reset-inst-${b.id}-${i}`} confirmText="Pembayaran termin ini dihapus permanen. Termin kembali Belum Dibayar." />}
                  <AdminActions editPath={`/admin/sub/installments/${b.id}/${i}`} deletePath={`/admin/sub/installments/${b.id}/${i}`} fields={INST_FIELDS} values={it} onDone={onDone} title="Edit Termin" testId={`admin-inst-${b.id}-${i}`} confirmText="Termin dihapus permanen dari rencana pembayaran." />
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canPlan && (b.status === "pending" || b.status === "planned") && <PlanBillDialog bill={b} onDone={onDone} />}
          {canPay && b.status !== "paid" && <PartialPayDialog bill={b} onDone={onDone} AccountSelect={PayAccountSelect} />}
          {canPay && b.status !== "paid" && <PayBillDialog bill={b} onDone={onDone} />}
          {canDelete && <EditBillDialog bill={b} vendors={vendors} companies={companies} onDone={onDone} />}
          {canEditOwn && <EditBillDialog bill={b} vendors={vendors} companies={companies} onDone={onDone} staff />}
          {(b.status === "paid" || (b.partial_payments || []).length > 0 || (b.payment_installments || []).some((i) => i.paid)) && <AdminDeleteButton path={`/admin/reset/bill-payment/${b.id}`} onDone={onDone} label="Hapus Pembayaran" testId={`admin-reset-billpay-${b.id}`} confirmText="Semua riwayat pembayaran tagihan ini dihapus permanen. Tagihan kembali Belum Lunas." />}
          {b.status === "planned" && <AdminDeleteButton path={`/admin/reset/bill-plan/${b.id}`} onDone={onDone} label="Hapus Rencana" testId={`admin-reset-billplan-${b.id}`} confirmText="Rencana pembayaran Direktur (dan pembayaran termin) dihapus permanen. Tagihan kembali ke status Menunggu." />}
          {canDelete && <AdminDeleteButton path={`/admin/records/vendor-bills/${b.id}`} onDone={onDone} iconOnly testId={`delete-bill-btn-${b.id}`} confirmText="Tagihan vendor beserta rencana dan riwayat pembayarannya dihapus permanen dan hilang dari semua laporan." />}
          {b.status === "paid" && b.payment_proof_file_id && (
            <a href={fileUrl(b.payment_proof_file_id)} target="_blank" rel="noreferrer"
              className="text-xs text-brand hover:underline inline-flex items-center gap-1" data-testid={`bill-proof-${b.id}`}>
              Bukti <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VendorBills() {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [ptFilter, setPtFilter] = useState(() => new URLSearchParams(window.location.search).get("pt") || "all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [notaQ, setNotaQ] = useState("");
  const [buyFrom, setBuyFrom] = useState("");
  const [buyTo, setBuyTo] = useState("");
  const [openV, setOpenV] = useState({});
  const [sortDir, setSortDir] = useState("desc");
  const [dupOnly, setDupOnly] = useState(false);
  const [monthMode, setMonthMode] = useState("");

  const canCreate = ["staf_tagihan", "it_admin", "direktur"].includes(user?.role);
  const canPlan = ["direktur", "it_admin"].includes(user?.role);
  const canPay = ["tim_keuangan", "direktur", "it_admin"].includes(user?.role);
  const canDelete = user?.role === "it_admin";
  const bulk = useBulk();
  const [paySel, setPaySel] = useState({});
  const togglePay = (b) => setPaySel((p) => { const n = { ...p }; if (n[b.id]) delete n[b.id]; else n[b.id] = true; return n; });

  const load = useCallback((silent) => {
    if (silent !== true) setLoading(true);
    api.get("/vendor-bills").then((r) => setBills(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const loadVendors = useCallback(() => {
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load(); loadVendors();
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, [load, loadVendors]);
  useAutoRefresh(load);

  const nq = notaQ.trim().toLowerCase();
  const baseFiltered = bills.filter((b) => (tab === "all" || b.status === tab) &&
    (!dupOnly || (b.duplicate_notas || []).length > 0) &&
    (ptFilter === "all" || (ptFilter === "none" ? !b.company_id : b.company_id === ptFilter)) &&
    (vendorFilter === "all" || b.vendor_name === vendorFilter) &&
    (!nq || (b.notas || []).some((n) => (n.nomor_nota || "").toLowerCase().includes(nq)) || (b.invoice_number || "").toLowerCase().includes(nq)) &&
    ((!buyFrom && !buyTo) || (b.notas || []).some((n) => { const d = (n.order_date || "").slice(0, 10); return d && (!buyFrom || d >= buyFrom) && (!buyTo || d <= buyTo); })));
  const filtered = monthMode ? baseFiltered.filter((b) => (monthMode === "month" ? dueThisMonth(b) : overduePrev(b)) > 0) : baseFiltered;
  const vendorNames = useMemo(() => [...new Set([...vendors.map((v) => v.name), ...bills.map((b) => b.vendor_name)].filter(Boolean))].sort((a, c) => a.localeCompare(c)), [vendors, bills]);
  const filterActive = vendorFilter !== "all" || ptFilter !== "all" || !!nq || !!buyFrom || !!buyTo || !!monthMode;
  const sumTotal = filtered.reduce((s, b) => s + (b.amount || 0), 0);
  const sumUnpaid = filtered.reduce((s, b) => s + (b.remaining_amount || 0), 0);
  const payBills = bills.filter((b) => paySel[b.id] && b.status !== "paid");
  const dupBillCount = bills.filter((b) => (b.duplicate_notas || []).length > 0).length;

  const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.name, v])), [vendors]);

  const groups = useMemo(() => {
    const key = (b) => (b.notas || []).reduce((m, n) => ((n.order_date || "").slice(0, 10) > m ? (n.order_date || "").slice(0, 10) : m), "") || (b.created_at || "").slice(0, 10);
    const cmp = (a, c) => (sortDir === "desc" ? c.localeCompare(a) : a.localeCompare(c));
    const map = {};
    for (const b of filtered) (map[b.vendor_name] ||= []).push(b);
    const arr = Object.entries(map).map(([vendor, items]) => {
      const sorted = [...items].sort((a, c) => cmp(key(a), key(c)) || cmp(a.created_at || "", c.created_at || ""));
      const latest = key(sorted[0]);
      const unpaidTotal = items.reduce((s, x) => s + (x.remaining_amount || 0), 0);
      const notaCount = items.reduce((s, x) => s + (x.notas?.length || 0), 0);
      const total = items.reduce((s, x) => s + (x.amount || 0), 0);
      const dueMonth = items.reduce((s, x) => s + dueThisMonth(x), 0);
      return { vendor, items: sorted, latest, unpaidTotal, count: items.length, notaCount, total, dueMonth };
    });
    arr.sort((a, c) => cmp(a.latest, c.latest));
    return arr;
  }, [filtered, sortDir]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <ReceiptText className="h-6 w-6 text-brand" /> Tagihan Vendor
          </h2>
          <p className="text-sm text-muted-foreground">Kelola tagihan vendor masuk, rencana & eksekusi pembayaran</p>
        </div>
        {canCreate && (
          <div className="flex flex-wrap gap-2">
            <ManageVendorsDialog vendors={vendors} onChange={loadVendors} />
            <CreateBillDialog vendors={vendors} companies={companies} onVendorsChange={loadVendors} onDone={load} />
          </div>
        )}
      </div>

      <MonthSummaryCard bills={baseFiltered} mode={monthMode} setMode={setMonthMode} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab} className="w-full sm:w-auto min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="bills-tabs-scroll">
          <TabsList data-testid="bills-tabs" className="w-max">
            <TabsTrigger value="all" data-testid="bill-tab-all">Semua</TabsTrigger>
            <TabsTrigger value="pending" data-testid="bill-tab-pending">Menunggu Rencana</TabsTrigger>
            <TabsTrigger value="planned" data-testid="bill-tab-planned">Rencana Ditetapkan</TabsTrigger>
            <TabsTrigger value="paid" data-testid="bill-tab-paid">Sudah Dibayar</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
        <div className="relative w-full sm:w-auto">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input value={notaQ} onChange={(e) => setNotaQ(e.target.value)} placeholder="Cari No. Nota" className="h-9 pl-9 w-full sm:w-48" data-testid="bill-nota-search" />
        </div>
        <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm flex-1 sm:flex-none min-w-0 sm:max-w-[11rem]" data-testid="bill-vendor-filter">
          <option value="all">Semua Vendor</option>
          {vendorNames.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={ptFilter} onChange={(e) => setPtFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm flex-1 sm:flex-none min-w-0 sm:max-w-[11rem]" data-testid="bill-pt-filter">
          <option value="all">Semua PT</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          <option value="none">Tanpa PT</option>
        </select>
        <select value={sortDir} onChange={(e) => setSortDir(e.target.value)} title="Urutkan berdasarkan Tanggal Pembelian"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm w-full sm:w-auto" data-testid="bill-sort">
          <option value="desc">Terbaru → Terlama</option>
          <option value="asc">Terlama → Terbaru</option>
        </select>
        {(
          <div className="flex items-center gap-1.5 w-full sm:w-auto" data-testid="bill-buy-date-filter">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Tgl Pembelian</span>
            <Input type="date" value={buyFrom} onChange={(e) => setBuyFrom(e.target.value)} className="h-9 flex-1 min-w-0 sm:flex-none sm:w-36" data-testid="bill-buy-from" />
            <span className="text-xs text-muted-foreground">s/d</span>
            <Input type="date" value={buyTo} onChange={(e) => setBuyTo(e.target.value)} className="h-9 flex-1 min-w-0 sm:flex-none sm:w-36" data-testid="bill-buy-to" />
          </div>
        )}
        </div>
      </div>
      {filterActive && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border bg-secondary/50 px-4 py-2 text-xs" data-testid="bill-filter-summary">
          <span><b data-testid="bill-filter-count">{filtered.length}</b> tagihan</span>
          <span>Total <b className="font-mono" data-testid="bill-filter-total">{formatRupiah(sumTotal)}</b></span>
          <span>Belum dibayar <b className="font-mono text-rose-700" data-testid="bill-filter-unpaid">{formatRupiah(sumUnpaid)}</b></span>
          <button type="button" onClick={() => { setVendorFilter("all"); setPtFilter("all"); setNotaQ(""); setBuyFrom(""); setBuyTo(""); setMonthMode(""); }} className="ml-auto text-brand hover:underline" data-testid="bill-filter-reset">Reset filter</button>
        </div>
      )}

      {dupBillCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-4 py-2.5 text-sm text-rose-800" data-testid="bill-dup-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Terdeteksi <b data-testid="bill-dup-count">{dupBillCount}</b> tagihan dengan nomor nota duplikat (vendor yang sama).</span>
          <button type="button" onClick={() => setDupOnly((v) => !v)} className="ml-auto rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-semibold hover:bg-rose-100" data-testid="bill-dup-toggle">
            {dupOnly ? "Tampilkan semua tagihan" : "Tampilkan duplikat saja"}
          </button>
        </div>
      )}

      {user?.role === "staf_tagihan" && <DupWhitelistDialog onDone={load} refreshKey={bills} />}
      <BulkBar bulk={bulk} onDone={load} label="tagihan" />
      {canPay && <BulkPayBar bills={payBills} onClear={() => setPaySel({})} onDone={load} />}
      {loading ? (
        <div className="space-y-2.5" data-testid="vendor-loading">{[0, 1, 2, 3].map((i) => (<div key={i} className={`h-4 rounded-md skeleton-shimmer animate-fade-in-up stagger-${i + 1}`} />))}</div>
      ) : groups.length === 0 ? (
        <Card className="text-center py-10 text-muted-foreground" data-testid="bills-empty">Belum ada tagihan.</Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => {
            const vinfo = vendorMap[g.vendor];
            const limit = vinfo?.credit_limit || 0;
            const remaining = limit - g.unpaidTotal;
            const ratio = limit > 0 ? g.unpaidTotal / limit : 0;
            const over = limit > 0 && g.unpaidTotal >= limit;
            const near = limit > 0 && !over && ratio >= 0.8;
            const collapsible = true;
            const isOpen = !collapsible || !!openV[g.vendor] || dupOnly || !!monthMode;
            return (
              <Card className="p-0 overflow-hidden" key={g.vendor} data-testid={`vendor-group-${g.vendor}`}>
                <div role={collapsible ? "button" : undefined} tabIndex={collapsible ? 0 : undefined}
                  onClick={collapsible ? () => setOpenV((p) => ({ ...p, [g.vendor]: !p[g.vendor] })) : undefined}
                  className={`flex items-center justify-between gap-3 px-4 py-3 bg-secondary/60 ${isOpen ? "border-b" : ""} ${collapsible ? "cursor-pointer hover:bg-secondary transition-colors" : ""}`}
                  data-testid={`vendor-toggle-${g.vendor}`}>
                  <div className="min-w-0 flex items-center gap-2">
                    {collapsible && <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`} />}
                    <div className="min-w-0">
                    <p className="font-semibold truncate" style={{ fontFamily: "Outfit" }}>{g.vendor}</p>
                    <p className="text-xs text-muted-foreground" data-testid={`vendor-count-${g.vendor}`}>{g.count} tagihan · {g.notaCount} nota{collapsible ? <> · Total <b className="font-mono text-foreground whitespace-nowrap" data-testid={`vendor-total-${g.vendor}`}>{formatRupiah(g.total)}</b> · Jatuh Tempo Bulan Ini <b className={`font-mono whitespace-nowrap ${g.dueMonth > 0 ? "text-amber-700" : "text-foreground"}`} data-testid={`vendor-due-month-${g.vendor}`}>{formatRupiah(g.dueMonth)}</b></> : ""}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[11px] text-muted-foreground">Belum Dibayar</p>
                    <p className="font-mono font-bold text-brand" data-testid={`vendor-unpaid-${g.vendor}`}>{formatRupiah(g.unpaidTotal)}</p>
                    {limit > 0 && (
                      <p className={`text-[11px] font-mono ${over ? "text-rose-600" : near ? "text-amber-600" : "text-muted-foreground"}`} data-testid={`vendor-limit-remaining-${g.vendor}`}>
                        {over ? `Lewat limit ${formatRupiah(g.unpaidTotal - limit)}` : `Sisa limit ${formatRupiah(remaining)}`} / {formatRupiah(limit)}
                      </p>
                    )}
                  </div>
                </div>
                {isOpen && (over || near) && (
                  <div className={`flex items-center gap-2 px-4 py-2 text-xs font-medium border-b ${over ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`} data-testid={`vendor-limit-alert-${g.vendor}`}>
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {over
                      ? `Tagihan belum dibayar sudah MELEBIHI limit kredit (${Math.round(ratio * 100)}% dari ${formatRupiah(limit)}).`
                      : `Tagihan belum dibayar mendekati limit kredit (${Math.round(ratio * 100)}% dari ${formatRupiah(limit)}).`}
                  </div>
                )}
                {isOpen && <div className="divide-y">
                  {bulk.enabled && (
                    <div className="px-4 py-1.5 text-[11px] text-muted-foreground flex items-center gap-2 bg-secondary/30">
                      <BulkCheckAll bulk={bulk} entries={g.items.map((b) => [b.id, { kind: "vendor-bills", id: b.id }])} testId={`bulk-bill-all-${g.vendor}`} /> Pilih semua tagihan {g.vendor}
                    </div>
                  )}
                  {g.items.map((b) => (
                    <BillRow key={b.id} b={b} canPlan={canPlan} canPay={canPay} canDelete={canDelete} onDone={load} vendors={vendors} companies={companies} bulk={bulk} isStaff={user?.role === "staf_tagihan"} paySel={paySel} togglePay={togglePay} />
                  ))}
                </div>}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

const curYm = () => new Date().toLocaleDateString("sv-SE").slice(0, 7);
const billDuePart = (x, test) => {
  if (x.status === "paid") return 0;
  const insts = x.payment_installments || [];
  const v = insts.length
    ? insts.filter((i) => !i.paid && i.date && test((i.date || "").slice(0, 7))).reduce((t, i) => t + (i.amount || 0), 0)
    : (x.due_date && test((x.due_date || "").slice(0, 7)) ? (x.remaining_amount || 0) : 0);
  return Math.min(v, x.remaining_amount || 0);
};
const dueThisMonth = (x) => { const ym = curYm(); return billDuePart(x, (m) => m === ym); };
const overduePrev = (x) => { const ym = curYm(); return billDuePart(x, (m) => m < ym); };

function MonthSummaryCard({ bills, mode, setMode }) {
  const mk = (fn) => { const l = bills.filter((b) => fn(b) > 0); return { total: l.reduce((s, b) => s + fn(b), 0), count: l.length, vendors: new Set(l.map((b) => b.vendor_name)).size }; };
  const m = mk(dueThisMonth);
  const o = mk(overduePrev);
  const monthName = new Date().toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const Part = ({ k, title, d, cls, testId }) => (
    <button type="button" onClick={() => setMode(mode === k ? "" : k)} data-testid={testId}
      className={`flex-1 min-w-[220px] text-left rounded-lg border px-4 py-3 transition-colors ${mode === k ? "ring-2 ring-offset-1 ring-brand" : ""} ${cls}`}>
      <p className="text-[11px] font-mono uppercase tracking-wider opacity-80">{title}</p>
      <p className="text-xl font-bold font-mono mt-0.5" data-testid={`${testId}-total`}>{formatRupiah(d.total)}</p>
      <p className="text-xs mt-0.5" data-testid={`${testId}-meta`}>{d.count} tagihan · {d.vendors} vendor{mode === k ? " · klik lagi untuk tampilkan semua" : " · klik untuk lihat"}</p>
    </button>
  );
  return (
    <Card className="p-3 flex flex-wrap gap-3" data-testid="month-summary-card">
      <Part k="month" title={`Jatuh Tempo Bulan Ini (${monthName})`} d={m} cls="border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100" testId="month-summary-due" />
      <Part k="overdue" title="Terlambat (bulan sebelumnya)" d={o} cls="border-rose-200 bg-rose-50 text-rose-900 hover:bg-rose-100" testId="month-summary-overdue" />
    </Card>
  );
}

function DupWhitelistDialog({ onDone, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const load = () => api.get("/vendor-bills/dup-whitelist").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  const undo = async (id) => {
    try { await api.delete(`/vendor-bills/dup-whitelist/${id}`); toast.success("Penandaan dibatalkan."); load(); onDone(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <button type="button" className="text-xs text-brand hover:underline inline-flex items-center gap-1 self-start" data-testid="dup-whitelist-btn"><CheckCircle2 className="h-3.5 w-3.5" /> Nota ditandai sah ({rows.length})</button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="dup-whitelist-dialog">
        <DialogHeader><DialogTitle>Nota Ditandai Sah</DialogTitle>
          <DialogDescription>Nomor nota yang ditandai bukan duplikat. Batalkan untuk memunculkan peringatan lagi.</DialogDescription></DialogHeader>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center" data-testid="dup-whitelist-empty">Belum ada nota yang ditandai sah.</p> : (
          <ul className="divide-y max-h-80 overflow-y-auto">
            {rows.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 py-2 text-sm" data-testid={`dup-whitelist-row-${r.id}`}>
                <div className="min-w-0"><p className="font-medium truncate">{r.nomor_nota} · {r.vendor_name}</p>
                  <p className="text-xs text-muted-foreground">{(r.bill_ids || []).length} tagihan · oleh {r.marked_by} · {formatDate(r.marked_at)}</p></div>
                <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 shrink-0" onClick={() => undo(r.id)} data-testid={`dup-whitelist-undo-${r.id}`}>Batalkan</Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateBillDialog({ onDone, vendors, companies, onVendorsChange }) {
  const parseAmt = (v) => Number(String(v ?? "").replace(/\D/g, "")) || 0;
  const fmtAmt = (v) => (String(v ?? "").replace(/\D/g, "") ? parseAmt(v).toLocaleString("id-ID") : "");
  const [open, setOpen] = useState(false);
  const [vendor, setVendor] = useState("");
  const [companyId, setCompanyId] = useState("");
  const company = companies.find((c) => c.id === companyId);
  const [type, setType] = useState("jatuh_tempo");
  const [days, setDays] = useState("");
  const [desc, setDesc] = useState("");
  const [notas, setNotas] = useState([{ nomor_nota: "", amount: "", order_date: "" }]);
  const [busy, setBusy] = useState(false);
  const baseDate = notas[0]?.order_date || "";
  const effDays = type === "harian" ? 0 : days;
  const dueDate = (() => {
    if (!baseDate || effDays === "" || Number(effDays) < 0) return "";
    const [y, m, d] = baseDate.split("-").map(Number);
    const dt = new Date(y, m - 1, d + Number(effDays));
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  })();

  const addNota = () => setNotas((p) => [...p, { nomor_nota: "", amount: "", order_date: "" }]);
  const updateNota = (i, k, v) => setNotas((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeNota = (i) => setNotas((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p));

  const validCount = notas.filter((n) => parseAmt(n.amount) > 0).length;
  const total = notas.reduce((s, n) => s + parseAmt(n.amount), 0);
  const nk = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const [dupDb, setDupDb] = useState({});
  const nomorsKey = notas.map((n) => n.nomor_nota).join("|");
  useEffect(() => {
    const nomors = notas.map((n) => n.nomor_nota.trim()).filter((s) => nk(s));
    if (!open || !vendor.trim() || !nomors.length) { setDupDb({}); return undefined; }
    const t = setTimeout(() => {
      api.post("/vendor-bills/check-duplicates", { vendor_name: vendor, nomors })
        .then((r) => setDupDb(Object.fromEntries(Object.entries(r.data.results || {}).map(([k, v]) => [nk(k), v])))).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [open, vendor, nomorsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const dupInfo = (i) => {
    const k = nk(notas[i].nomor_nota);
    if (!k) return null;
    const db = dupDb[k] || [];
    const inForm = notas.some((x, j) => j !== i && nk(x.nomor_nota) === k);
    return db.length || inForm ? { db, inForm } : null;
  };
  const dupCount = notas.filter((_, i) => dupInfo(i)).length;

  const reset = () => {
    setVendor(""); setCompanyId(""); setType("jatuh_tempo"); setDays(""); setDesc("");
    setNotas([{ nomor_nota: "", amount: "", order_date: "" }]);
  };

  const submit = async (e) => {
    e.preventDefault();
    const cleanNotas = notas
      .filter((n) => parseAmt(n.amount) > 0)
      .map((n) => ({ nomor_nota: n.nomor_nota.trim(), amount: parseAmt(n.amount), order_date: n.order_date || null }));
    if (!companyId) { toast.error("Pilih PT terlebih dahulu"); return; }
    if (!vendor.trim()) { toast.error("Lengkapi nama vendor"); return; }
    if (!baseDate) { toast.error("Isi Tanggal Pemesanan nota pertama"); return; }
    if (!dueDate) { toast.error("Isi Jatuh Tempo (jumlah hari)"); return; }
    if (cleanNotas.length === 0) { toast.error("Tambahkan minimal satu nota dengan jumlah > 0"); return; }
    let nDup = dupCount;
    try {
      const nomors = cleanNotas.map((n) => n.nomor_nota).filter((s) => nk(s));
      if (nomors.length) {
        const r = await api.post("/vendor-bills/check-duplicates", { vendor_name: vendor, nomors });
        const res = Object.fromEntries(Object.entries(r.data.results || {}).map(([k, v]) => [nk(k), v]));
        setDupDb(res);
        nDup = nomors.filter((s, i) => (res[nk(s)] || []).length || nomors.some((x, j) => j !== i && nk(x) === nk(s))).length;
      }
    } catch { /* fallback to last known check */ }
    if (nDup > 0 && !window.confirm(`Terdeteksi ${nDup} nomor nota duplikat untuk vendor ${vendor}. Tetap simpan tagihan ini?`)) return;
    setBusy(true);
    try {
      await api.post("/vendor-bills", { vendor_name: vendor, company_id: companyId, payment_type: type, due_date: dueDate, due_days: Number(effDays), description: desc, notas: cleanNotas });
      toast.success(`Tagihan tersimpan (${cleanNotas.length} nota, total ${formatRupiah(total)}).`);
      reset(); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-brand hover:bg-emerald-800" data-testid="add-bill-btn"><Plus className="h-4 w-4 mr-2" /> Input Tagihan</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="create-bill-dialog">
        <DialogHeader><DialogTitle>Input Tagihan Vendor</DialogTitle>
          <DialogDescription>Satu vendor bisa punya beberapa nota. Total & tanggal jatuh tempo dihitung otomatis.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
          <div>
            <Label>Nama Vendor</Label>
            <VendorCombobox vendors={vendors} value={vendor} onChange={setVendor} onVendorCreated={onVendorsChange} />
          </div>
          <div>
            <Label>Pilih PT <span className="text-red-500">*</span></Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="mt-1.5" data-testid="bill-company-select"><SelectValue placeholder={companies.length ? "Pilih PT" : "Belum ada PT di Master PT"} /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`bill-company-option-${c.id}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {company && (
              <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs space-y-1" data-testid="bill-company-account">
                <p className="text-muted-foreground">Rekening PT (referensi pembayaran):</p>
                {(company.accounts || []).map((a, i) => (
                  <p key={i} className="font-mono text-brand" data-testid={`bill-company-account-${i}`}>
                    <span className="font-semibold">{a.bank_name || "Bank"}</span> · {a.account_number}{a.account_holder ? ` · a.n. ${a.account_holder}` : ""}
                  </p>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Daftar Nota Tagihan</Label>
              <Button type="button" size="sm" variant="outline" onClick={addNota} data-testid="bill-add-nota">
                <Plus className="h-3.5 w-3.5 mr-1" /> Tambah Nota
              </Button>
            </div>
            <div className="hidden sm:grid grid-cols-[1.1fr_1fr_1.1fr_auto] gap-2 text-[11px] text-muted-foreground font-medium px-0.5">
              <span>No. Nota</span><span>Tgl Pemesanan</span><span>Jumlah Tagihan</span><span></span>
            </div>
            {notas.map((n, i) => {
              const dup = dupInfo(i);
              return (
              <div key={i} className="space-y-1">
              <div className="relative grid grid-cols-2 sm:grid-cols-[1.1fr_1fr_1.1fr_auto] gap-2 items-center rounded-lg border p-2 sm:border-0 sm:p-0" data-testid={`bill-nota-row-${i}`}>
                <Input placeholder="No. Nota" value={n.nomor_nota} onChange={(e) => updateNota(i, "nomor_nota", e.target.value)} className={`order-1 col-span-2 sm:col-span-1 w-[calc(100%-2.75rem)] sm:w-full ${dup ? "border-rose-400 focus-visible:ring-rose-400" : ""}`} data-testid={`bill-nota-nomor-${i}`} />
                <Input type="date" title="Tgl Pemesanan" value={n.order_date} onChange={(e) => updateNota(i, "order_date", e.target.value)} className="order-3 sm:order-2 min-w-0" data-testid={`bill-nota-date-${i}`} />
                <Input type="text" inputMode="numeric" placeholder="Jumlah" value={n.amount} onChange={(e) => updateNota(i, "amount", e.target.value.replace(/[^\d.]/g, ""))}
                  onBlur={(e) => updateNota(i, "amount", fmtAmt(e.target.value))} className="font-mono order-4 sm:order-3 min-w-0" data-testid={`bill-nota-amount-${i}`} />
                <Button type="button" size="icon" variant="ghost" onClick={() => removeNota(i)} disabled={notas.length === 1} className="order-2 sm:order-4 absolute top-2 right-2 sm:static" data-testid={`bill-nota-remove-${i}`}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
              {dup && (
                <div className="rounded-md border border-rose-300 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-800" data-testid={`bill-nota-dup-${i}`}>
                  <p className="font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Nota "{n.nomor_nota}" terdeteksi duplikat</p>
                  {dup.inForm && <p>Nomor ini diketik lebih dari sekali di form ini.</p>}
                  {dup.db.map((e, j) => <p key={j}>Sudah ada: {e.nomor_nota} · {e.company_name || "Tanpa PT"} · {formatRupiah(e.amount)} · diinput {formatDate(e.created_at)}</p>)}
                </div>
              )}
              </div>
              );
            })}
            <div className="flex items-center justify-between text-sm pt-2 border-t mt-1" data-testid="bill-total">
              <span className="text-muted-foreground">Total Tagihan ({validCount} nota)</span>
              <span className="font-mono font-semibold text-brand">{formatRupiah(total)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Cara Bayar</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1.5" data-testid="bill-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="harian" data-testid="bill-type-harian">Harian</SelectItem>
                  <SelectItem value="jatuh_tempo" data-testid="bill-type-jatuh_tempo">Jatuh Tempo</SelectItem>
                  <SelectItem value="bilyet_giro" data-testid="bill-type-bilyet_giro">Bilyet Giro (BG)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jatuh Tempo (hari)</Label>
              <div className="relative mt-1.5">
                <Input type="number" min="0" placeholder="30" value={type === "harian" ? 0 : days} disabled={type === "harian"}
                  onChange={(e) => setDays(e.target.value)} className="pr-12 font-mono" data-testid="bill-due-days-input" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">hari</span>
              </div>
            </div>
          </div>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs -mt-1" data-testid="bill-due-preview">
            {dueDate ? (
              <span>{type === "bilyet_giro" ? "Tanggal Efektif BG" : "Tanggal Jatuh Tempo"}: <span className="font-semibold text-brand" data-testid="bill-due-preview-date">{formatDate(dueDate)}</span>
                <span className="text-muted-foreground"> (Tgl Pemesanan {formatDate(baseDate)} + {Number(effDays)} hari)</span></span>
            ) : (
              <span className="text-muted-foreground">Isi Tgl Pemesanan nota pertama dan jumlah hari untuk menghitung tanggal jatuh tempo.</span>
            )}
          </div>

          <div>
            <Label>Keterangan</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1.5" data-testid="bill-desc-input" placeholder="Keterangan tagihan..." />
          </div>
          {dupCount > 0 && (
            <div className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800 flex gap-2" data-testid="bill-dup-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>Peringatan: {dupCount} nomor nota terdeteksi duplikat untuk vendor ini. Periksa kembali; tagihan tetap bisa disimpan jika memang diperlukan.</span>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="submit-bill-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Tagihan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NotaBreakdown({ bill }) {
  const notas = [...(bill.notas || [])].sort((a, c) => (a.order_date || "").localeCompare(c.order_date || ""));
  if (notas.length === 0) return null;
  return (
    <div className="rounded-lg border bg-secondary/40 p-3" data-testid={`nota-breakdown-${bill.id}`}>
      <p className="text-xs font-semibold text-brand mb-1.5">Rincian Nota ({notas.length})</p>
      <div className="space-y-1">
        {notas.map((n, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-xs" data-testid={`nota-item-${bill.id}-${i}`}>
            <span className="min-w-0 truncate">
              <span className="font-medium">{n.nomor_nota || `Nota ${i + 1}`}</span>
              {n.order_date && <span className="text-muted-foreground"> · pesan {formatDate(n.order_date)}</span>}
            </span>
            <span className="font-mono shrink-0">{formatRupiah(n.amount)}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between text-xs font-semibold pt-1.5 mt-1.5 border-t">
        <span>Total</span><span className="font-mono text-brand">{formatRupiah(bill.amount)}</span>
      </div>
    </div>
  );
}

function PlanBillDialog({ bill, onDone }) {
  const isEdit = bill.status === "planned";
  const [open, setOpen] = useState(false);
  const [schedule, setSchedule] = useState(bill.payment_schedule || "");
  const [note, setNote] = useState(bill.payment_plan_note || "");
  const [plannedDate, setPlannedDate] = useState((bill.planned_date || "").slice(0, 10));
  const [rows, setRows] = useState(
    (bill.payment_installments && bill.payment_installments.length)
      ? bill.payment_installments.map((r) => ({ label: r.label || "", date: (r.date || "").slice(0, 10), amount: String(r.amount || "") }))
      : []
  );
  const [busy, setBusy] = useState(false);

  const addRow = () => setRows((p) => [...p, { label: "", date: "", amount: "" }]);
  const updateRow = (i, k, v) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

  const totalTermin = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const diff = bill.amount - totalTermin;

  const submit = async (e) => {
    e.preventDefault();
    if (!schedule) { toast.error("Pilih jadwal rencana pembayaran"); return; }
    const installments = rows
      .filter((r) => Number(r.amount) > 0)
      .map((r) => ({ label: r.label.trim(), date: r.date || null, amount: Number(r.amount) }));
    if (schedule !== "jatuh_tempo" && installments.length === 0) {
      toast.error("Tambahkan minimal satu termin nominal pembayaran"); return;
    }
    setBusy(true);
    try {
      await api.post(`/vendor-bills/${bill.id}/plan`, { schedule, note, planned_date: plannedDate || null, installments });
      toast.success(isEdit ? "Rencana pembayaran diperbarui." : "Rencana pembayaran ditetapkan.");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-brand text-brand" data-testid={`plan-bill-btn-${bill.id}`}>
          <ClipboardList className="h-4 w-4 mr-1" /> {isEdit ? "Edit Rencana" : "Rencana"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`plan-bill-dialog-${bill.id}`}>
        <DialogHeader><DialogTitle>{isEdit ? "Edit" : "Buat"} Rencana Pembayaran — {bill.vendor_name}</DialogTitle>
          <DialogDescription>Tetapkan jadwal & nominal termin. Cocokkan dengan rincian nota di bawah.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">Jumlah {formatRupiah(bill.amount)} · {PAYMENT_TYPE_LABEL[bill.payment_type]}</p>
          <NotaBreakdown bill={bill} />
          <div>
            <Label>Jadwal Pembayaran</Label>
            <Select value={schedule} onValueChange={setSchedule}>
              <SelectTrigger className="mt-1.5" data-testid={`plan-schedule-select-${bill.id}`}><SelectValue placeholder="Pilih jadwal pembayaran" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="harian" data-testid={`plan-schedule-harian-${bill.id}`}>Setiap Hari</SelectItem>
                <SelectItem value="mingguan" data-testid={`plan-schedule-mingguan-${bill.id}`}>Setiap Minggu</SelectItem>
                <SelectItem value="jatuh_tempo" data-testid={`plan-schedule-jatuh_tempo-${bill.id}`}>Menunggu Jatuh Tempo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {schedule && schedule !== "jatuh_tempo" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Rincian Nominal per Termin</Label>
                <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid={`plan-add-termin-${bill.id}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Tambah
                </Button>
              </div>
              {rows.length === 0 && (
                <p className="text-xs text-muted-foreground">Belum ada termin. Klik "Tambah", contoh: Senin Rp 500.000, Selasa Rp 300.000, dst.</p>
              )}
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1.2fr_auto] gap-2 items-center" data-testid={`plan-termin-row-${bill.id}-${i}`}>
                  <Input placeholder="Label (Senin)" value={r.label} onChange={(e) => updateRow(i, "label", e.target.value)} data-testid={`plan-termin-label-${bill.id}-${i}`} />
                  <Input type="date" value={r.date} onChange={(e) => updateRow(i, "date", e.target.value)} data-testid={`plan-termin-date-${bill.id}-${i}`} />
                  <RupiahInput placeholder="Nominal" value={r.amount} onChange={(e) => updateRow(i, "amount", e.target.value)} className="font-mono" data-testid={`plan-termin-amount-${bill.id}-${i}`} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeRow(i)} data-testid={`plan-termin-remove-${bill.id}-${i}`}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              ))}
              {rows.length > 0 && (
                <>
                  <div className="flex items-center justify-between text-sm pt-1" data-testid={`plan-termin-total-${bill.id}`}>
                    <span className="text-muted-foreground">Total Termin</span>
                    <span className="font-mono font-semibold">{formatRupiah(totalTermin)}</span>
                  </div>
                  {diff !== 0 && (
                    <p className={`text-xs ${diff > 0 ? "text-amber-600" : "text-rose-600"}`}>
                      {diff > 0 ? `Kurang ${formatRupiah(diff)} dari total tagihan` : `Lebih ${formatRupiah(-diff)} dari total tagihan`}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <Label>Catatan (opsional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid={`plan-note-input-${bill.id}`} placeholder="Contoh: transfer bertahap tiap Senin" />
          </div>
          <div>
            <Label>Tanggal Mulai (opsional)</Label>
            <Input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} className="mt-1.5" data-testid={`plan-date-input-${bill.id}`} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid={`submit-plan-btn-${bill.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Perbarui Rencana" : "Tetapkan Rencana")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function payAccLabel(a) {
  if (a.account_number === "TUNAI") return "Tunai Pusat";
  return [a.bank_name, a.account_number, a.account_holder ? `a.n. ${a.account_holder}` : ""].filter(Boolean).join(" ");
}

function PayAccountSelect({ accounts, value, onChange, amount, testId }) {
  if (!accounts.length) return <p className="text-xs text-muted-foreground" data-testid={`${testId}-none`}>PT tagihan ini belum punya rekening — pembayaran tanpa potong saldo rekening.</p>;
  const acc = accounts.find((a) => a.account_number === value);
  const short = acc && acc.balance < amount;
  return (
    <div>
      <Label>Pilih Sumber Pembayaran (Tunai Pusat / Rekening) <span className="text-red-500">*</span></Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5" data-testid={`${testId}-select`}><SelectValue placeholder="Pilih Tunai Pusat atau rekening PT" /></SelectTrigger>
        <SelectContent>
          {accounts.map((a) => (
            <SelectItem key={a.account_number} value={a.account_number} data-testid={`${testId}-option-${a.account_number}`}>
              <span className={a.is_cash ? "font-semibold" : "font-mono"}>{a.is_cash ? "Tunai Pusat" : payAccLabel(a)}</span>
              <span className={`ml-2 text-xs ${a.balance < amount ? "text-red-600" : "text-emerald-700"}`}>Saldo {formatRupiah(a.balance)}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {acc && (
        <p className="text-xs mt-1 text-muted-foreground" data-testid={`${testId}-after`}>Saldo setelah bayar: <span className="font-mono">{formatRupiah(acc.balance - amount)}</span></p>
      )}
      {short && acc.is_cash && (
        <div className="mt-2 flex gap-2 rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800" data-testid={`${testId}-cash-blocked`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Saldo Tunai Pusat tidak mencukupi (kurang {formatRupiah(amount - acc.balance)}). Pilih sumber lain.</span>
        </div>
      )}
      {short && !acc.is_cash && (
        <div className="mt-2 flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid={`${testId}-warning`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Saldo rekening tidak mencukupi (kurang {formatRupiah(amount - acc.balance)}). Pembayaran tetap bisa dikonfirmasi dan saldo menjadi minus.</span>
        </div>
      )}
    </div>
  );
}

function PayBillDialog({ bill, onDone }) {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accNo, setAccNo] = useState("");
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [insts, setInsts] = useState(bill.payment_installments || []);
  const [activeIdx, setActiveIdx] = useState(-1);

  const hasInstallments = (bill.payment_installments?.length || 0) > 0;
  const paidTotal = insts.filter((x) => x.paid).reduce((s, x) => s + (x.amount || 0), 0);

  const loadAccounts = async () => {
    try {
      const res = await api.get(`/vendor-bills/${bill.id}/accounts`);
      const accs = res.data.accounts || [];
      const all = res.data.cash_balance != null ? [{ bank_name: "Tunai Pusat", account_number: "TUNAI", account_holder: "", balance: res.data.cash_balance, is_cash: true }, ...accs] : accs;
      setAccounts(all); setAccNo(all.length === 1 ? all[0].account_number : "");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const needAcc = (amt) => {
    if (accounts.length && !accNo) { toast.error("Pilih sumber pembayaran"); return true; }
    const a = accounts.find((x) => x.account_number === accNo);
    if (a?.is_cash && a.balance + 0.001 < amt) { toast.error("Saldo Tunai Pusat tidak mencukupi"); return true; }
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) { toast.error("Upload bukti pembayaran"); return; }
    if (needAcc(bill.remaining_amount ?? bill.amount ?? 0)) return;
    setBusy(true);
    try {
      await api.post(`/vendor-bills/${bill.id}/pay`, { file_id: files[0].file_id, note, account_number: accNo });
      toast.success("Pembayaran tagihan tercatat.");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const payTermin = async (i) => {
    if (!files.length) { toast.error("Upload bukti pembayaran termin"); return; }
    if (needAcc(insts[i]?.amount || 0)) return;
    setBusy(true);
    try {
      const res = await api.post(`/vendor-bills/${bill.id}/pay-installment`, { index: i, file_id: files[0].file_id, account_number: accNo });
      setInsts(res.data.payment_installments || []);
      setActiveIdx(-1); setFiles([]);
      loadAccounts();
      toast.success("Pembayaran termin tercatat.");
      onDone();
      if (res.data.status === "paid") { toast.success("Semua termin lunas — tagihan selesai."); setOpen(false); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setInsts(bill.payment_installments || []); setActiveIdx(-1); setFiles([]); loadAccounts(); } }}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-brand hover:bg-emerald-800" data-testid={`pay-bill-btn-${bill.id}`}>
          <Wallet className="h-4 w-4 mr-1" /> Bayar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`pay-bill-dialog-${bill.id}`}>
        <DialogHeader><DialogTitle>Eksekusi Pembayaran — {bill.vendor_name}</DialogTitle>
          <DialogDescription>Unggah bukti pembayaran. Cocokkan nominal dengan rincian nota di bawah.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Total tagihan {formatRupiah(bill.amount)}{bill.paid_amount > 0 ? ` · Terbayar ${formatRupiah(bill.paid_amount)} · Sisa ${formatRupiah(bill.remaining_amount)}` : ""}{bill.payment_schedule ? ` · ${SCHEDULE_LABELS[bill.payment_schedule] || bill.payment_schedule}` : ""}</p>
          <NotaBreakdown bill={bill} />

          {hasInstallments ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-brand">Bayar per termin sesuai rencana Direktur:</p>
              {insts.map((it, i) => (
                <div key={i} className="border rounded-lg p-3" data-testid={`pay-termin-${bill.id}-${i}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{it.label || `Termin ${i + 1}`}</p>
                      <p className="text-xs text-muted-foreground">{it.date ? formatDate(it.date) : "Tanpa tanggal"} · <span className="font-mono">{formatRupiah(it.amount)}</span></p>
                    </div>
                    {it.paid ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`pay-termin-paid-${bill.id}-${i}`}>
                        <CheckCircle2 className="h-4 w-4" /> Lunas
                      </span>
                    ) : activeIdx === i ? null : (
                      <Button size="sm" className="bg-brand hover:bg-emerald-800" onClick={() => { setActiveIdx(i); setFiles([]); }} data-testid={`pay-termin-btn-${bill.id}-${i}`}>
                        <Wallet className="h-4 w-4 mr-1" /> Bayar
                      </Button>
                    )}
                  </div>
                  {it.paid && it.proof_url && (
                    <a href={fileUrl(it.proof_url)} target="_blank" rel="noreferrer" className="text-xs text-brand inline-flex items-center gap-1 mt-1"><ExternalLink className="h-3 w-3" /> Lihat bukti</a>
                  )}
                  {it.paid && it.pay_account && <p className="text-xs text-muted-foreground mt-1" data-testid={`pay-termin-account-${bill.id}-${i}`}>Dibayar dari: <span className="font-mono">{payAccLabel(it.pay_account)}</span></p>}
                  {activeIdx === i && !it.paid && (
                    <div className="mt-3 space-y-2">
                      <PayAccountSelect accounts={accounts} value={accNo} onChange={setAccNo} amount={it.amount || 0} testId={`pay-termin-account-${bill.id}-${i}`} />
                      <FileUploader files={files} onChange={setFiles} testId={`pay-termin-uploader-${bill.id}-${i}`} />
                      <div className="flex gap-2">
                        <Button size="sm" disabled={busy} className="bg-brand hover:bg-emerald-800" onClick={() => payTermin(i)} data-testid={`confirm-termin-btn-${bill.id}-${i}`}>
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Konfirmasi Bayar Termin"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setActiveIdx(-1); setFiles([]); }}>Batal</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between text-sm pt-1" data-testid={`pay-termin-progress-${bill.id}`}>
                <span className="text-muted-foreground">Terbayar</span>
                <span className="font-mono font-semibold">{formatRupiah(paidTotal)} / {formatRupiah(bill.amount)}</span>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {bill.payment_plan && (
                <div className="text-sm bg-secondary/60 rounded-lg p-3 border">
                  <p className="font-medium text-brand">Rencana Direktur:</p>
                  <p className="text-muted-foreground">{bill.payment_plan}</p>
                </div>
              )}
              <PayAccountSelect accounts={accounts} value={accNo} onChange={setAccNo} amount={bill.remaining_amount ?? bill.amount ?? 0} testId={`pay-account-${bill.id}`} />
              <div>
                <Label>Upload Bukti Pembayaran <span className="text-red-500">*</span></Label>
                <div className="mt-1.5"><FileUploader files={files} onChange={setFiles} testId={`pay-uploader-${bill.id}`} /></div>
              </div>
              <div>
                <Label>Catatan (opsional)</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid={`pay-note-input-${bill.id}`} />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid={`submit-pay-btn-${bill.id}`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Konfirmasi Pembayaran"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
